<script setup>
import { ref, computed, onMounted } from 'vue';
import {
  useFunctionGetter,
  useMapGetter,
  useStore,
} from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Integration from './Integration.vue';
import Spinner from 'shared/components/Spinner.vue';
import GoogleSheetImportsList from './GoogleSheetImportsList.vue';

const { t } = useI18n();
const store = useStore();
const integrationLoaded = ref(false);

const integration = useFunctionGetter('integrations/getIntegration', 'google_sheets');
const googleContactsIntegration = useFunctionGetter('integrations/getIntegration', 'google_contacts');

const uiFlags = useMapGetter('integrations/getUIFlags');

const integrationAction = computed(() => {
  if (googleContactsIntegration.value.enabled) {
    return 'disconnect';
  }

  return '';
});

const initializeGoogleSheetsIntegration = async () => {
  await store.dispatch('integrations/get');
  integrationLoaded.value = true;
};

onMounted(() => {
  initializeGoogleSheetsIntegration();
});
</script>

<template>
  <div class="flex-grow flex-shrink p-4 overflow-auto mx-auto w-full max-w-6xl">
    <div v-if="integrationLoaded">
      <Integration
        :integration-id="integration.id"
        :integration-logo="integration.logo"
        :integration-name="integration.name"
        :integration-description="integration.description"
        :integration-enabled="googleContactsIntegration.enabled"
        :integration-action="integrationAction"
        :delete-confirmation-text="{
          title: t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.DELETE.TITLE'),
          message: t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.DELETE.MESSAGE'),
          confirm: t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.DELETE.CONFIRM'),
          cancel: t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.DELETE.CANCEL'),
        }"
      >
        <template #action>
          <router-link
            v-if="!googleContactsIntegration.enabled"
            :to="{ name: 'settings_integrations_google_contacts' }"
          >
            <Button
              faded
              blue
              :label="t('INTEGRATION_SETTINGS.CONNECT.BUTTON_TEXT')"
            />
          </router-link>
        </template>
        <template #settings>
          <div
            v-if="!googleContactsIntegration.enabled"
            class="border-t border-n-weak p-6 bg-n-alpha-1 flex flex-col gap-4 rounded-b-md"
          >
            <div class="flex items-center gap-3 p-4 bg-n-amber-2 border border-n-amber-3 rounded-lg text-n-amber-11">
              <i class="i-lucide-alert-triangle text-xl flex-shrink-0" />
              <div>
                <h4 class="font-semibold text-sm">
                  {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.CONNECTION_REQUIRED.TITLE') }}
                </h4>
                <p class="text-xs mt-0.5">
                  {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.CONNECTION_REQUIRED.DESCRIPTION') }}
                </p>
              </div>
            </div>
            <div class="flex justify-end">
              <router-link :to="{ name: 'settings_integrations_google_contacts' }">
                <Button
                  blue
                  :label="t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.CONNECTION_REQUIRED.BUTTON')"
                />
              </router-link>
            </div>
          </div>
          <GoogleSheetImportsList v-else />
        </template>
      </Integration>
    </div>
    <div v-else class="flex items-center justify-center flex-1">
      <Spinner size="" color-scheme="primary" />
    </div>
  </div>
</template>
